import { useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import EventCalendar from "parts/Calendar";
import NextEvents from "components/calendar/NextEvents";
import { getTaskSchedule, CalendarCardEvent, CalendarEvent } from "@shared/api/tasks";
import { LanguageContext } from "context";
import { useSessionContext } from "context/SessionContext";
import { useNotification } from "context/NotificationContext";

import { getUserTimeZone, getMonthStartDateTimeInZone } from "../../utils/dateHelper";

function Calendar(): JSX.Element {
  const { t } = useContext(LanguageContext) as any;
  const { session } = useSessionContext();
  const { showNotification } = useNotification();

  const [calendarEvents, setCalendarEvents] = useState<CalendarCardEvent[]>([]);
  const [thisMonthTasks, setThisMonthTasks] = useState<CalendarEvent[]>([]);
  const [nextMonthTasks, setNextMonthTasks] = useState<CalendarEvent[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [lastErrorMonth, setLastErrorMonth] = useState<string | null>(null);

  const today = useMemo(() => new Date(), []);
  const initialDisplayDate = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), [today]);
  const [currentDisplayDate, setCurrentDisplayDate] = useState<Date>(initialDisplayDate);

  const lastFetchedMonthRef = useRef<string>("");

  const userTimeZone = useMemo(() => {
    return getUserTimeZone();
  }, []);
  
  const fetchAndFilterTasks = useCallback(
    async (dateToFetch: Date) => {
      const currentMonthKey = `${dateToFetch.getFullYear()}-${dateToFetch.getMonth()}`;

      if (isFetching || lastFetchedMonthRef.current === currentMonthKey) {
        console.log("Calendar.tsx: Already fetching or already fetched this month. Skipping new request.");
        return;
      }

      if (lastErrorMonth === currentMonthKey) {
        console.log(`Calendar.tsx: Skipping fetch for ${currentMonthKey} due to recent error.`);
        return;
      }

      setIsFetching(true);
      setLastErrorMonth(null);

      try {
        if (!session?.access_token) {
          console.log(`Calendar.tsx: Session access_token is empty. Cannot fetch tasks.`);
          return;
        }

        const year = dateToFetch.getFullYear();
        const month = dateToFetch.getMonth() + 1;

        console.log(`Calendar.tsx: Fetching tasks for Year: ${year}, Month: ${month}`);

        const data = await getTaskSchedule(session.access_token, year, month);

        const weeklyAiTasksForCalendar = data
          .filter((event) => event.task_type === "weekly_ai_task")
          .map((event) => ({
            id: event.id,
            title: event.title,
            category: event.category,
            start: event.planned_start!,
            end: event.deadline!,
            className: event.className,
            allDay: true,
          }));

        setCalendarEvents(weeklyAiTasksForCalendar);

        const filteredThisMonthTasks: CalendarEvent[] = [];
        const filteredNextMonthTasks: CalendarEvent[] = [];

        const displayYear = dateToFetch.getFullYear();
        const displayMonth = dateToFetch.getMonth();

        data.forEach((event) => {
          if (event.task_type === "monthly_ai_task" && event.planned_start) {
            const plannedStartDate = new Date(event.planned_start);
            const eventMonth = plannedStartDate.getMonth();
            const eventYear = plannedStartDate.getFullYear();

            if (eventYear === displayYear && eventMonth === displayMonth) {
              filteredThisMonthTasks.push(event);
            } else if (
              (eventYear === displayYear && eventMonth === displayMonth + 1) ||
              (displayMonth === 11 && eventYear === displayYear + 1 && eventMonth === 0)
            ) {
              filteredNextMonthTasks.push(event);
            }
          }
        });

        setThisMonthTasks(filteredThisMonthTasks);
        setNextMonthTasks(filteredNextMonthTasks);
        lastFetchedMonthRef.current = currentMonthKey;
      } catch (error) {
        console.error("Calendar.tsx: Error loading calendar events:", error);
        showNotification({
          icon: "error",
          title: t("Common.error"),
          message: t("Common.updated-error"),
        }, 2000);
        setLastErrorMonth(currentMonthKey);
      } finally {
        setIsFetching(false);
      }
    },
    [session?.access_token, showNotification, t, isFetching, lastErrorMonth]
  );

  useEffect(() => {
    if (session?.access_token) {
      console.log(`Calendar.tsx: useEffect triggered. Current display date for fetch: ${currentDisplayDate.toISOString()}`);
      fetchAndFilterTasks(currentDisplayDate);
    }
    return () => {
      console.log("Calendar.tsx: Calendar component unmounting.");
    };
  }, [fetchAndFilterTasks, session?.access_token, currentDisplayDate]);

  const handleDatesSet = useCallback((arg: { view: any }) => {
    const viewStartDate = arg.view.currentStart; // ← FullCalendar の正確な月初
    const normalized = getMonthStartDateTimeInZone(viewStartDate, userTimeZone);
    const prev = getMonthStartDateTimeInZone(currentDisplayDate, userTimeZone);

    console.log("Calendar.tsx: handleDatesSet called.");
    console.log("view.currentStart (UTC):", viewStartDate.toISOString());
    console.log("normalized (local month start):", normalized.toISO());
    console.log("prev currentDisplayDate (local):", prev.toISO());

    if (!normalized.equals(prev)) {
      console.log("Calendar.tsx: --> Month/Year changed. Updating currentDisplayDate state.");
      setCurrentDisplayDate(normalized.toJSDate());
    } else {
      console.log("Calendar.tsx: --> Month/Year is the same. No date state change.");
    }
  }, [currentDisplayDate, userTimeZone]);

  const validRangeStart = useMemo(() => {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return start.toISOString().split("T")[0];
  }, [today]);

  const validRangeEnd = useMemo(() => {
    const end = new Date(today.getFullYear(), today.getMonth() + 36 + 1, 0);
    return end.toISOString().split("T")[0];
  }, [today]);

  const memoizedCalendar = useMemo(() => (
    <EventCalendar
      initialView="dayGridMonth"
      events={calendarEvents}
      selectable
      timeZone={userTimeZone}
      validRange={{
        start: validRangeStart,
        end: validRangeEnd,
      }}
      datesSet={handleDatesSet}
    />
  ), [calendarEvents, validRangeStart, validRangeEnd, handleDatesSet]);

  return (
    <MDBox pt={3}>
      <Grid container spacing={3}>
        <Grid item xs={12} xl={9} sx={{ height: "max-content" }}>
          {memoizedCalendar}
        </Grid>
        <Grid item xs={12} xl={3}>
          <MDBox mb={3}>
            <NextEvents
              thisMonthTasks={thisMonthTasks}
              nextMonthTasks={nextMonthTasks}
            />
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );
}

export default Calendar;
